import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DatabaseBackup, Download } from 'lucide-react';
import { formatDateTime } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState } from '@/components/ui/misc';
import { DataTable, type Column } from '@/components/ui/table';
import { toast } from 'sonner';

interface BackupRow {
  name: string;
  sizeBytes: number;
  createdAt: string;
}

/** Tamaño legible: una copia de poco peso también se puede leer sin sacar la calculadora. */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(1)} ${units[index]}`;
}

/**
 * Copias de la base desde Administración. El fichero se crea en el servidor (volumen
 * `/data/backups`) y desde aquí solo se lista y se descarga: no hay restauración, para
 * no ofrecer un botón que reemplace la base de un clic.
 */
export function BackupsPanel() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const backupsQuery = useQuery({
    queryKey: ['admin-backups'],
    queryFn: () => api<{ data: BackupRow[] }>('/admin/backups')
  });

  const create = async () => {
    setBusy(true);
    try {
      const created = await api<BackupRow>('/admin/backups', { method: 'POST' });
      toast.success(`Copia creada: ${created.name} (${humanSize(created.sizeBytes)})`);
      await qc.invalidateQueries({ queryKey: ['admin-backups'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  if (backupsQuery.error) {
    return <ErrorState message={(backupsQuery.error as Error).message} onRetry={() => void backupsQuery.refetch()} />;
  }

  const rows = backupsQuery.data?.data ?? [];
  const columns: Column<BackupRow>[] = [
    {
      key: 'createdAt',
      header: 'Fecha',
      cell: (row) => <span className="tabular-nums">{formatDateTime(row.createdAt)}</span>
    },
    {
      key: 'name',
      header: 'Fichero',
      cell: (row) => <span className="font-mono text-[11px]">{row.name}</span>
    },
    {
      key: 'sizeBytes',
      header: 'Tamaño',
      className: 'text-right',
      cell: (row) => <span className="tabular-nums">{humanSize(row.sizeBytes)}</span>
    },
    {
      key: 'download',
      header: '',
      className: 'text-right',
      cell: (row) => (
        <a
          href={`/api/admin/backups/${encodeURIComponent(row.name)}/download`}
          className="inline-flex min-h-[36px] items-center gap-2 rounded-md border border-border px-3 text-sm font-medium hover:border-primary"
          download
        >
          <Download className="size-4" /> Descargar
        </a>
      )
    }
  ];

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Copias de la base de datos</h3>
          <p className="text-xs text-muted-foreground">
            Se guardan en el servidor, en <code>BACKUP_DIR</code> (volumen <code>/data/backups</code>), con
            30 días de retención. Cada creación y cada descarga quedan en la auditoría.
          </p>
        </div>
        <Button size="lg" className="min-h-[44px]" disabled={busy} onClick={() => void create()}>
          <DatabaseBackup /> {busy ? 'Creando copia…' : 'Crear copia ahora'}
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          title="Todavía no hay copias"
          description="Pulsa «Crear copia ahora» para generar la primera. Las que deja el pipeline en cada despliegue viven en el servidor y no se listan aquí."
          icon={DatabaseBackup}
        />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(row) => row.name} />
      )}
    </Card>
  );
}
